import { useState, useCallback, useEffect, useRef } from "react";
import { useForm, type Resolver, type DefaultValues } from "react-hook-form";
import { useRouter } from "expo-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { step1Schema, type Step1Values } from "../schemas/preVettingSchema";
import { usePreVettingDraft } from "./usePreVettingDraft";
import { useDebouncedDraftSave } from "./useDebouncedDraftSave";
import { useRestoredDocumentFile } from "@/hooks/useRestoredDocumentFile";
import { preVettingApi } from "@/lib/api/preVetting";
import type { FileUploadState, SelectedFile } from "@/components/ui/FileUpload";
import type { TranslationKeys } from "@chinese-vendor/i18n";

const DEFAULT_DIAL_CODE = "+86";

/**
 * Given a stored phone string like "+86138…", split it into
 * { dialCode: "+86", localNumber: "138…" }.
 * Falls back to the default dial code when no recognisable prefix is found.
 */
function parseStoredPhone(stored: string | undefined | null): {
  dialCode: string;
  localNumber: string;
} {
  if (!stored) return { dialCode: DEFAULT_DIAL_CODE, localNumber: "" };
  // Match a leading "+" followed by 1-4 digits (covers +1 … +1684)
  const match = stored.match(/^(\+\d{1,4})(.*)$/);
  if (match) return { dialCode: match[1], localNumber: match[2] };
  return { dialCode: DEFAULT_DIAL_CODE, localNumber: stored };
}

export function usePreVettingStep1Form() {
  const router = useRouter();
  const { draftQuery, saveDraftMutation } = usePreVettingDraft();
  const scheduleSave = useDebouncedDraftSave(saveDraftMutation.mutateAsync);

  // Dial-code state shared with the screen via returned helpers.
  const dialCodeRef = useRef(DEFAULT_DIAL_CODE);
  const [initialDialCode, setInitialDialCode] = useState(DEFAULT_DIAL_CODE);

  const setDialCode = useCallback((code: string) => {
    dialCodeRef.current = code;
  }, []);

  /** Prepend dial code to contact_phone for persistence. */
  const withDialCode = useCallback(
    (values: Record<string, unknown>): Record<string, unknown> => {
      const phone = values.contact_phone;
      if (typeof phone === "string" && phone.length > 0 && !phone.startsWith("+")) {
        return { ...values, contact_phone: `${dialCodeRef.current}${phone}` };
      }
      return values;
    },
    [],
  );

  const form = useForm<Step1Values>({
    resolver: zodResolver(step1Schema) as Resolver<Step1Values>,
    defaultValues: {
      legal_name_chinese: "",
      legal_name_english: "",
      nationality: "",
      role_in_company: "",
      department: "",
      contact_phone: "",
      contact_email: "",
      legal_representative_id_type: "passport",
      legal_representative_id_document: null,
      wechat_qr_string: null,
    } as unknown as DefaultValues<Step1Values>,
    mode: "onBlur",
  });

  const [idDocumentFile, setIdDocumentFile] = useState<SelectedFile | null>(null);
  const [idDocumentUploadState, setIdDocumentUploadState] = useState<FileUploadState>("idle");
  const [idDocumentUploadError, setIdDocumentUploadError] = useState<TranslationKeys | undefined>(
    undefined
  );
  const [wechatQrFile, setWechatQrFile] = useState<SelectedFile | null>(null);
  const [wechatQrUploadState, setWechatQrUploadState] = useState<FileUploadState>("idle");
  const [wechatQrUploadError, setWechatQrUploadError] = useState<TranslationKeys | undefined>(
    undefined
  );

  // Restore file previews from saved S3 keys on draft load
  const { file: restoredIdDoc, uploadState: restoredIdDocState } = useRestoredDocumentFile(
    draftQuery.isSuccess ? (draftQuery.data?.step1?.legal_representative_id_document ?? null) : null
  );
  const { file: restoredWechatQr, uploadState: restoredWechatQrState } = useRestoredDocumentFile(
    draftQuery.isSuccess ? (draftQuery.data?.step1?.wechat_qr_string ?? null) : null
  );

  useEffect(() => {
    if (restoredIdDoc && idDocumentUploadState === "idle" && !idDocumentFile) {
      setIdDocumentFile(restoredIdDoc);
      setIdDocumentUploadState(restoredIdDocState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoredIdDoc]);

  useEffect(() => {
    if (restoredWechatQr && wechatQrUploadState === "idle" && !wechatQrFile) {
      setWechatQrFile(restoredWechatQr);
      setWechatQrUploadState(restoredWechatQrState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoredWechatQr]);

  // Populate form once draft has loaded
  const VALID_ID_TYPES = ["passport", "national_id", "other"] as const;
  useEffect(() => {
    if (draftQuery.isSuccess && draftQuery.data?.step1) {
      const draftStep1 = draftQuery.data.step1 as Partial<Step1Values>;
      const rawIdType = draftStep1.legal_representative_id_type;
      const sanitizedIdType = VALID_ID_TYPES.includes(rawIdType as (typeof VALID_ID_TYPES)[number])
        ? rawIdType
        : "passport";

      // Restore dial code from saved phone (e.g. "+966500…" → "+966")
      const { dialCode: savedDialCode, localNumber } = parseStoredPhone(
        draftStep1.contact_phone,
      );
      dialCodeRef.current = savedDialCode;
      setInitialDialCode(savedDialCode);

      form.reset({
        ...form.getValues(),
        ...draftStep1,
        contact_phone: localNumber,
        legal_representative_id_type: sanitizedIdType,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftQuery.isSuccess]);

  const onFieldBlur = useCallback(() => {
    scheduleSave({ step1: withDialCode(form.getValues()) });
  }, [scheduleSave, form, withDialCode]);

  const handleIdDocumentFile = useCallback(
    async (file: SelectedFile) => {
      setIdDocumentFile(file);
      setIdDocumentUploadState("uploading");
      setIdDocumentUploadError(undefined);
      try {
        const key = await preVettingApi.uploadDocument(file);
        form.setValue("legal_representative_id_document", key);
        onFieldBlur();
        setIdDocumentUploadState("success");
      } catch {
        setIdDocumentUploadState("error");
        setIdDocumentUploadError("fileUpload.uploadFailed");
      }
    },
    [form, onFieldBlur]
  );

  const handleIdDocumentRemove = useCallback(() => {
    setIdDocumentFile(null);
    setIdDocumentUploadState("idle");
    setIdDocumentUploadError(undefined);
    form.setValue("legal_representative_id_document", null);
  }, [form]);

  const handleWechatQrFile = useCallback(
    async (file: SelectedFile) => {
      setWechatQrFile(file);
      setWechatQrUploadState("uploading");
      setWechatQrUploadError(undefined);
      try {
        const key = await preVettingApi.uploadDocument(file);
        form.setValue("wechat_qr_string", key);
        onFieldBlur();
        setWechatQrUploadState("success");
      } catch {
        setWechatQrUploadState("error");
        setWechatQrUploadError("fileUpload.uploadFailed");
      }
    },
    [form, onFieldBlur]
  );

  const handleWechatQrRemove = useCallback(() => {
    setWechatQrFile(null);
    setWechatQrUploadState("idle");
    setWechatQrUploadError(undefined);
    form.setValue("wechat_qr_string", null as unknown as string);
  }, [form]);

  const handleNext = form.handleSubmit(async (values) => {
    await saveDraftMutation.mutateAsync({ step1: withDialCode(values) });
    router.push("/(vendor)/pre-vetting/step-2");
  });

  return {
    form,
    onFieldBlur,
    handleNext,
    /** Dial code parsed from the saved draft (e.g. "+86"). Use to initialise UI state. */
    initialDialCode,
    /** Call whenever the user picks a different country dial code. */
    setDialCode,
    idDocumentFile,
    idDocumentUploadState,
    idDocumentUploadError,
    handleIdDocumentFile,
    handleIdDocumentRemove,
    wechatQrFile,
    wechatQrUploadState,
    wechatQrUploadError,
    handleWechatQrFile,
    handleWechatQrRemove,
  };
}
